import { useElementBounding } from '@vueuse/core';
import debounce from 'lodash-es/debounce';
import { computed, nextTick, type Ref, ref } from 'vue';

import { DEBOUNCE } from '../../constants';
import { toElement } from '../../utils/templateRefNarrowing';
import { DATA_ATTRIBUTES } from './constants';
import { applyElementVisibility, createElementIdMap, syncMoreActionsVisibility } from './utils';

// Accept Ref<unknown> for template refs so untyped useTemplateRef() can be passed
interface UseOverflowCalculatorOptions {
  actionsContainerRef: Ref<unknown>;
  overflowIds: Ref<Set<string>>;
  moreDropdownMenuRef: Ref<unknown>;
  trackerElementRef: Ref<unknown>;
  onOverflowChange?: () => void;
}

export function useOverflowCalculator({
  actionsContainerRef,
  overflowIds,
  moreDropdownMenuRef,
  trackerElementRef,
  onOverflowChange,
}: UseOverflowCalculatorOptions) {
  const isProcessing = ref(false);
  const lastOverflowState = ref<string>(''); // Track last overflow state to prevent unnecessary updates
  const trackerElRef = computed(() => toElement(trackerElementRef.value));
  const trackerBounding = useElementBounding(trackerElRef);

  /**
   * Updates overflow state and applies visibility classes to elements
   */
  function updateOverflowState(
    newOverflowIds: Set<string>,
    directChildren: HTMLElement[],
    elementIdMap: Map<HTMLElement, string>,
  ): void {
    overflowIds.value.clear();
    newOverflowIds.forEach((id) => overflowIds.value.add(id));

    // Apply visibility classes
    directChildren.forEach((element) => {
      const elementId = elementIdMap.get(element) || element.getAttribute(DATA_ATTRIBUTES.ACTION_ID) || undefined;

      if (elementId) {
        applyElementVisibility(element, elementId, overflowIds.value);
      }
    });
  }

  function calculateOverflowInternal() {
    const container = toElement(actionsContainerRef.value);
    const tracker = toElement(trackerElementRef.value);
    if (!container || !tracker) {
      isProcessing.value = false;
      return;
    }

    const directChildren = Array.from(container.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
    const elementIdMap = createElementIdMap(directChildren);

    // Create a new overflow set to compare with current state
    const newOverflowIds = new Set<string>();

    // Get current gap width from CSS computed styles of the actions container
    const computedStyle = window.getComputedStyle(container);
    const gapValue = computedStyle.columnGap || computedStyle.gap;
    const gapWidth = gapValue ? parseFloat(gapValue) : 0;

    // Calculate which elements should be hidden based on their position relative to tracker
    directChildren.forEach((element, index) => {
      const elementId = elementIdMap.get(element) || element.getAttribute(DATA_ATTRIBUTES.ACTION_ID) || undefined;

      const elementRect = element.getBoundingClientRect();

      // Calculate the effective right edge including gap
      // If this is not the last element, add gap to the right edge
      const effectiveRightEdge = index < directChildren.length - 1 ? elementRect.right + gapWidth : elementRect.right;

      // If element's effective right edge extends beyond tracker's left edge, hide it
      if (effectiveRightEdge > trackerBounding.left.value) {
        if (elementId) {
          newOverflowIds.add(elementId);
        }
      }
    });

    // Check if overflow state has actually changed
    const currentOverflowState = Array.from(overflowIds.value).sort().join(',');
    const newOverflowState = Array.from(newOverflowIds).sort().join(',');

    if (currentOverflowState !== newOverflowState) {
      // Update overflow state and apply visibility
      updateOverflowState(newOverflowIds, directChildren, elementIdMap);

      // Sync more-actions content visibility
      syncMoreActionsVisibility(toElement(moreDropdownMenuRef.value), overflowIds.value);

      // Call callback if provided and state actually changed
      // Use nextTick to ensure CSS changes are applied before calculating visible elements width
      if (onOverflowChange && lastOverflowState.value !== newOverflowState) {
        lastOverflowState.value = newOverflowState;
        nextTick(() => {
          onOverflowChange();
        });
      }
    }

    isProcessing.value = false;
  }

  const debouncedCalculateOverflow = debounce(calculateOverflowInternal, DEBOUNCE.FAST);

  function calculateOverflow() {
    if (isProcessing.value || !toElement(actionsContainerRef.value) || !toElement(trackerElementRef.value)) {
      return;
    }

    isProcessing.value = true;
    debouncedCalculateOverflow();
  }

  function updateTrackerBounding() {
    trackerBounding.update();
  }

  function cleanup() {
    debouncedCalculateOverflow.cancel();
    isProcessing.value = false;
  }

  return {
    calculateOverflow,
    applyElementVisibility,
    updateOverflowState,
    updateTrackerBounding,
    cleanup,
    isProcessing,
  };
}
